import { useEffect, useRef } from "react";
import { useBackdropMotion } from "@shared/motion";
import { motionReduced, useAccessibility } from "@shared/ui/accessibility";

/**
 * Сколько раз в секунду сдвигаются пятна.
 *
 * Не каждый кадр, и это главное, что здесь сделано ради скорости. Пока пятна
 * движутся, всё стекло над ними пересчитывает размытие и преломление заново:
 * под ним изменился фон. CSS-анимация двигала пятна на каждом кадре экрана, и
 * видеоускоритель захлёбывался — 30 кадров в секунду в покое, 22 с открытыми
 * настройками. Двенадцать сдвигов в секунду дают те же 180–190, что и стоящие
 * пятна, а на глаз движения не отличить: пятно проходит свой путь за 24–40
 * секунд, и за один шаг сдвигается меньше чем на полтора пикселя мягкого края.
 */
const RATE = 12;


/**
 * Фон под всем приложением: основа и пять цветных пятен, которые медленно
 * плывут, — так в макете v6. Стеклу нужно, что размывать и преломлять, и
 * неподвижные обои давали ему для этого слишком мало: край линзы видно,
 * только когда под ним что-то меняется.
 *
 * Цвета берутся у выбранного тона (`--ui-blob-*`, см. `tokens.css`), путь и
 * время у каждого пятна свои (`base.css`). Пути описаны CSS-анимациями, но
 * идут они не сами: анимации стоят на паузе, а время им переводит таймер,
 * `RATE` раз в секунду. При «меньше движения» пятна стоят.
 *
 * Выключенное движение просто перестаёт переводить время: пятна остаются там,
 * где их застали, и не прыгают на исходные места.
 */
export function Backdrop() {
  const moving = useBackdropMotion((state) => state.on);
  // «Меньше движения» — своё из «Специальных возможностей» или системное.
  const calm = useAccessibility((state) => state.reduceMotion);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const blobs = box.current?.getAnimations({ subtree: true }) ?? [];
    blobs.forEach((blob) => blob.pause());
    if (!moving || motionReduced()) return;

    let last = performance.now();
    const id = window.setInterval(() => {
      const now = performance.now();
      const step = now - last;
      last = now;
      for (const blob of blobs) {
        const at = blob.currentTime;
        blob.currentTime = (typeof at === "number" ? at : 0) + step;
      }
    }, 1000 / RATE);

    return () => window.clearInterval(id);
  }, [moving, calm]);

  return (
    <div className="backdrop" ref={box} aria-hidden>
      <span className="backdrop-blob backdrop-blob-1" />
      <span className="backdrop-blob backdrop-blob-2" />
      <span className="backdrop-blob backdrop-blob-3" />
      <span className="backdrop-blob backdrop-blob-4" />
      <span className="backdrop-blob backdrop-blob-5" />
      <span className="backdrop-vignette" />
    </div>
  );
}
